import type {
  BalanceRegistration,
  BalanceSlot,
  BalanceSlotPosition,
  BalanceSlotStatus,
} from '@sportninja/common/services/teamBalance'
import { balanceLabel } from './balanceI18n'
import { isOpenPlayerSlot } from './balanceRowKind'

/**
 * The status a slot shows, sorts and filters by (Jeff, Oct 1): the server's own
 * lifecycle status. Being listed in the Player Finder is part of a slot's TYPE
 * (`balanceRowKind.slotType`), never its status, so the old client-side PLAYER
 * FINDER variants (D47) are gone. `confirmed` still READS "Invoiced".
 *
 * THIS MODULE IS STILL THE ONE PLACE the label, rank and value are derived — the
 * chip, the grid `valueGetter`, the filter and the sort comparator all read it, so
 * they can never disagree about what a row says.
 */
export type BalanceSlotDisplayStatus = BalanceSlotStatus

/**
 * Is this slot currently advertised in the Player Finder?
 *
 * Guarded on "still an open PLAYER slot" as well as the flag: the server clears
 * `finder_listed` when a player claims the slot, but a payload rendered mid-flight
 * (or an older api2 that does not send the field at all) must never show an ad on
 * an occupied row — nor on a team official's line (Sep 23), which is never a spot a
 * player can take.
 */
export const isSlotListedInFinder = (slot: BalanceSlot): boolean =>
  slot.finder_listed === true && isOpenPlayerSlot(slot)

/**
 * Is this listed slot's ad actually RUNNING?
 *
 * Listing is per slot, but a listed slot is only advertised while Player Finder
 * search shows the team's ad: the team's recruiting switch can be off (it never
 * unticks slots), the competition can turn Player Finder off, or the roster can
 * be full. The server answers with `finder_advertised`, using search's own rules;
 * an older api2 that does not send it had none of these, so a listed slot was
 * advertised.
 */
export const isSlotAdvertisedInFinder = (slot: BalanceSlot): boolean =>
  isSlotListedInFinder(slot) && (slot.finder_advertised ?? true)

/** Listed, but players cannot see its ad — `finderHiddenCause` says why. */
export const isSlotListedButHidden = (slot: BalanceSlot): boolean =>
  isSlotListedInFinder(slot) && !isSlotAdvertisedInFinder(slot)

/**
 * Review round 1, TF-03 — the ONE reason the sheet gives for a hidden ad.
 *
 * - `finder_disabled` — the competition turned Player Finder off. Named first:
 *   nothing the team does brings the ad back meanwhile (the server refuses to
 *   turn recruiting on), so any other advice would send the rep the wrong way.
 * - `registration_private` — SN-4709: the team is Private, and private means
 *   unlisted. The team's own deliberate choice, so it is named before the
 *   recruiting switch; its copy says other reasons may remain once it is public.
 * - `recruiting_off` — the team's own switch, changed in Player Finder settings.
 * - `roster_full` — search hides a team with no room for another player.
 * - `other` — nothing this build can explain; the settings sub-tab lists the
 *   server's reasons.
 */
export type FinderHiddenCause =
  | 'finder_disabled'
  | 'registration_private'
  | 'recruiting_off'
  | 'roster_full'
  | 'other'

const HIDDEN_CAUSE_PRECEDENCE: FinderHiddenCause[] = [
  'finder_disabled',
  'registration_private',
  'recruiting_off',
  'roster_full',
]

/**
 * From the server's `player_finder_hidden_reasons`. An older api2 sends none; its
 * only way to hide a listed slot was the recruiting switch.
 */
export const finderHiddenCause = (
  registration: Pick<
    BalanceRegistration,
    'player_finder_hidden_reasons' | 'player_finder_recruiting'
  >
): FinderHiddenCause => {
  const reasons = registration.player_finder_hidden_reasons
  if (reasons === undefined) {
    return registration.player_finder_recruiting === false
      ? 'recruiting_off'
      : 'other'
  }

  return (
    HIDDEN_CAUSE_PRECEDENCE.find((cause) => reasons.includes(cause)) ?? 'other'
  )
}

/** The display status the chip, sort and filter all agree on: the server's. */
export const slotDisplayStatus = (
  slot: Pick<BalanceSlot, 'status'>
): BalanceSlotDisplayStatus => slot.status

/**
 * The position a slot shows — three sources, never mixed: an OCCUPIED slot its
 * occupant's roster position, a LISTED OPEN slot the position it is recruiting for,
 * and a slot HELD for someone the position approval will roster them with (the one
 * the rep set, else last season's — Jeff, Oct 2, F2; Inon, Oct 2).
 */
const slotPositionOf = (slot: BalanceSlot): BalanceSlotPosition | null =>
  (isSlotListedInFinder(slot)
    ? slot.finder_position
    : slot.player
    ? slot.player.position
    : slot.invitee?.position) ?? null

/**
 * The POSITION column's value (see `slotPositionOf`). An ordinary open slot, or a
 * hold with no position yet, has none and yields '' so those rows group at one end
 * of a sort rather than scattering.
 */
export const slotPositionLabel = (slot: BalanceSlot): string => {
  const position = slotPositionOf(slot)

  return position?.name_full || position?.name || position?.abbreviation || ''
}

/**
 * Inon, Oct 2 — the id of the position the row shows (`slotPositionOf`), which the
 * double-click Position editor opens on and compares a pick against. Null when the
 * row shows none.
 */
export const slotPositionId = (slot: BalanceSlot): string | null =>
  slotPositionOf(slot)?.id ?? null

/**
 * Inon, Oct 2 — the NUMBER column's value, from the same sources as the position: an
 * occupant's number (their roster row's; before approval, the one approval will give
 * them), or the number the rep set on a hold. A listed open slot recruits for a
 * position, never a number. Null when there is none — jersey 0 is a real number.
 */
export const slotPlayerNumber = (slot: BalanceSlot): number | null => {
  if (isSlotListedInFinder(slot)) {
    return null
  }

  return (
    (slot.player ? slot.player.player_number : slot.invitee?.player_number) ??
    null
  )
}

/**
 * Lifecycle order for sorting. An invited slot is an open slot held for someone,
 * so it sits right after `open`.
 */
export const DISPLAY_STATUS_RANK: Record<BalanceSlotDisplayStatus, number> = {
  open: 0,
  invited: 1,
  pending: 2,
  confirmed: 3,
  paid: 4,
  cancelled: 5,
}

/**
 * Every display status in lifecycle order — derived from the ranks above rather
 * than re-listed, so the STATUS header's legend can never fall out of step with
 * the sort it is describing (or miss a status added later).
 */
export const DISPLAY_STATUS_ORDER = (
  Object.keys(DISPLAY_STATUS_RANK) as BalanceSlotDisplayStatus[]
).sort((a, b) => DISPLAY_STATUS_RANK[a] - DISPLAY_STATUS_RANK[b])

/**
 * The human label for every display status.
 *
 * SN-4709 (James, Jul 31) — CONFIRMED reads INVOICED: the wire value stays
 * `confirmed` and the rename is display-only. It lives HERE because this one
 * function feeds the chip, the grid `valueGetter`, the text filter and the sort
 * comparator — so the label can never drift between what a row says and what it
 * filters or sorts as.
 */
const STATUS_LABEL_KEYS: Record<BalanceSlotDisplayStatus, string> = {
  open: 'common:balanceSlotStatusOpen',
  invited: 'common:balanceSlotStatusInvited',
  pending: 'common:balanceSlotStatusPending',
  // "Invoiced", not "Confirmed" — the state IS "a payment request went out and a
  // balance is still due", which is what lockSlot() actually does.
  confirmed: 'common:balanceSlotStatusInvoiced',
  paid: 'common:balanceSlotStatusPaid',
  cancelled: 'common:balanceSlotStatusCancelled',
}

export const displayStatusLabel = (status: BalanceSlotDisplayStatus): string =>
  balanceLabel(STATUS_LABEL_KEYS[status] ?? STATUS_LABEL_KEYS.open)

/**
 * Lifecycle rank for a slot, derived from the SAME display status the chip and
 * the valueGetter use.
 *
 * Deliberately keyed on the slot rather than on the rendered label: the column's
 * sort value is the translated label (so the text filter matches what is on
 * screen), and reverse-mapping a label back to a status would collapse the
 * moment two labels coincided — which they do the instant a translation is
 * missing and both resolve to ''. Ranking from the row data cannot drift.
 */
export const slotDisplayRank = (slot: Pick<BalanceSlot, 'status'>): number =>
  DISPLAY_STATUS_RANK[slotDisplayStatus(slot)] ?? -1
